import { create } from 'zustand/index';

import { AuthReqDeviceCoordsDto } from '~modules/auth/model';
import { createAuto, updateAuto } from '~modules/journal/api';
import { JournalDto } from '~modules/journal/model';
import { getSettings } from '~modules/settings/api';
import { DeviceBlockIncognito } from '~modules/settings/model';
import { DeviceInfoDto, collectDeviceInfo } from '~modules/user-device/model';
import { getFileFromBase64 } from '~shared/helpers';

async function loadIncognitoBlocked(): Promise<boolean> {
	try {
		const setting = await getSettings(DeviceBlockIncognito);
		return setting.value === '1';
	} catch (err) {
		console.error(err);
		return false;
	}
}

interface JournalStore {
	isPending: boolean;
	journal: JournalDto | null;
	deviceInfo: DeviceInfoDto | null;
	/** Проверка устройства завершена - до этого момента сканер показывать нельзя */
	isDeviceChecked: boolean;
	isIncognitoBlocked: boolean;
	initDevice: () => Promise<DeviceInfoDto>;
	clear: () => void;
	create: (qr: string) => void;
	setPhoto: (photo: string) => void;
	coordinates: AuthReqDeviceCoordsDto | null;
	setPosition: (val: AuthReqDeviceCoordsDto) => void;
}

export const journalStore = create<JournalStore>((set, get) => ({
	isPending: true,
	journal: null,
	coordinates: null,
	deviceInfo: null,
	isDeviceChecked: false,
	isIncognitoBlocked: false,
	initDevice: async () => {
		const cached = get().deviceInfo;
		if (cached) return cached;

		try {
			const deviceInfo = await collectDeviceInfo();
			// Настройку запрашиваем только при обнаруженном приватном режиме - остальным она не нужна
			const isIncognitoBlocked = deviceInfo.is_incognito ? await loadIncognitoBlocked() : false;

			set(old => ({ ...old, deviceInfo, isIncognitoBlocked }));
			return deviceInfo;
		} finally {
			set(old => ({ ...old, isDeviceChecked: true }));
		}
	},
	clear: async () => {
		set(old => ({ ...old, journal: null, isPending: false }));
	},
	create: async (qr: string) => {
		const { coordinates, initDevice } = get();
		set(old => ({ ...old, isPending: true }));
		try {
			const deviceInfo = await initDevice();
			const data = await createAuto(qr, deviceInfo, coordinates);
			set(old => ({ ...old, journal: data, isPending: false }));
		} catch (err) {
			console.error(err);
			set(old => ({ ...old, isPending: false }));
		}
	},
	setPhoto: async (photo: string) => {
		const { journal, initDevice } = get();
		if (journal) {
			set(old => ({ ...old, isPending: true }));
			try {
				const file = getFileFromBase64(photo, 'photo.jpg');
				const data = await updateAuto(journal, file, await initDevice());
				set(old => ({ ...old, journal: data, isPending: false }));
			} catch (err) {
				console.error(err);
				set(old => ({ ...old, isPending: false }));
			}
		}
	},
	setPosition: (val: AuthReqDeviceCoordsDto) => set(old => ({ ...old, coordinates: val })),
}));
